import { useState, useEffect } from 'react';
import { expensesApi, Expense } from '../../api/expenses.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';

const ExpenseList = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [formData, setFormData] = useState({
    description: '',
    amount: 0,
    category: '',
    date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      setIsLoading(true);
      const data = await expensesApi.getAll();
      setExpenses(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Giderler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (expense?: Expense) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        description: expense.description,
        amount: expense.amount,
        category: expense.category || '',
        date: expense.date.split('T')[0],
      });
    } else {
      setEditingExpense(null);
      setFormData({
        description: '',
        amount: 0,
        category: '',
        date: new Date().toISOString().split('T')[0],
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingExpense(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingExpense) {
        await expensesApi.update(editingExpense.id, formData);
      } else {
        await expensesApi.create(formData);
      }
      await fetchExpenses();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu gideri silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await expensesApi.delete(id);
      await fetchExpenses();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  const columns: Column<Expense>[] = [
    { key: 'description', header: 'Açıklama' },
    { key: 'category', header: 'Kategori', render: (item) => item.category || '-' },
    { key: 'amount', header: 'Tutar', render: (item) => `₺${item.amount.toFixed(2)}` },
    {
      key: 'date',
      header: 'Tarih',
      render: (item) => new Date(item.date).toLocaleDateString('tr-TR'),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Giderler yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Giderler</h1>
        <Button onClick={() => handleOpenModal()}>Yeni Gider Ekle</Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <Table data={expenses} columns={columns} emptyMessage="Henüz gider bulunmamaktadır" />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingExpense ? 'Gider Düzenle' : 'Yeni Gider Ekle'}
      >
        <form onSubmit={handleSubmit}>
          <Input
            label="Açıklama"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
          />
          <Input
            label="Kategori"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          />
          <Input
            label="Tutar"
            type="number"
            step="0.01"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
            required
          />
          <Input
            label="Tarih"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
          />
          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              {editingExpense ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ExpenseList;
